import { useState, useMemo } from "react";
import { useData } from "../data/DataContext";
import { ContentHeader } from "../components/ContentHeader";
import { EntityCard } from "../components/EntityCard";
import { ESTATES } from "../nav";
import type { BackendOrSurfaceEntity } from "../api/types";

type SortBy = "health" | "tier" | "name";
const TIER_ORDER: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };

interface Props {
  estate: "backend" | "surfaces";
}

export function BackendOrSurfacesTab({ estate }: Props) {
  const data = useData();
  const [sortBy, setSortBy] = useState<SortBy>("health");

  const items: BackendOrSurfaceEntity[] = useMemo(
    () => (data.commandCenter?.estates[estate] ?? []),
    [data, estate],
  );

  const sorted = useMemo(() => {
    const arr = [...items];
    if (sortBy === "health") {
      const score = (it: BackendOrSurfaceEntity) =>
        data.coverageByName[it.name]?.health_score ?? 100;
      return arr.sort((a, b) =>
        score(a) - score(b)
        || (TIER_ORDER[a.tier ?? "P9"] ?? 9) - (TIER_ORDER[b.tier ?? "P9"] ?? 9));
    }
    if (sortBy === "name") return arr.sort((a, b) => a.name.localeCompare(b.name));
    return arr.sort((a, b) =>
      (TIER_ORDER[a.tier ?? "P9"] ?? 9) - (TIER_ORDER[b.tier ?? "P9"] ?? 9)
      || a.name.localeCompare(b.name));
  }, [items, sortBy, data.coverageByName]);

  const meta = ESTATES.find((e) => e.id === estate)!;

  if (sortBy === "tier") {
    return (
      <>
        <ContentHeader title={meta.label} count={items.length} description={meta.desc}
          right={<SortControl value={sortBy} onChange={setSortBy} />} />
        <TieredGroups items={sorted} />
      </>
    );
  }
  return (
    <>
      <ContentHeader title={meta.label} count={items.length} description={meta.desc}
        right={<SortControl value={sortBy} onChange={setSortBy} />} />
      <div className="grid">{sorted.map((it) => <EntityCard key={it.id} item={it} />)}</div>
    </>
  );
}

export function BackendTab() { return <BackendOrSurfacesTab estate="backend" />; }
export function SurfacesTab() { return <BackendOrSurfacesTab estate="surfaces" />; }

function SortControl({ value, onChange }: { value: SortBy; onChange: (v: SortBy) => void }) {
  return (
    <div className="sort-control">
      Sort by{" "}
      <select value={value} onChange={(e) => onChange(e.target.value as SortBy)}>
        <option value="health">Health (worst first)</option>
        <option value="tier">Tier (P0 first)</option>
        <option value="name">Name</option>
      </select>
    </div>
  );
}

function TieredGroups({ items }: { items: BackendOrSurfaceEntity[] }) {
  const groups: Record<string, BackendOrSurfaceEntity[]> = {};
  items.forEach((it) => {
    const t = it.tier ?? "no tier";
    (groups[t] = groups[t] ?? []).push(it);
  });
  return (
    <>
      {(["P0", "P1", "P2", "P3", "no tier"] as const).map((t) => {
        const list = groups[t];
        if (!list || list.length === 0) return null;
        return (
          <div key={t}>
            <div className="section-header"><h2>{t} · {list.length}</h2></div>
            <div className="grid">{list.map((it) => <EntityCard key={it.id} item={it} />)}</div>
          </div>
        );
      })}
    </>
  );
}
